import React, { useEffect, useState } from 'react';
import apiClient from '../api/client';
import { formatTargetTypeLabel } from '../utils/humanLabels';

export default function BidderRules() {
  const [campaigns, setCampaigns] = useState([]);
  const [selectedCampaign, setSelectedCampaign] = useState('');
  const [status, setStatus] = useState('');
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [targets, setTargets] = useState([]);
  const [targetsLoading, setTargetsLoading] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    fetchCampaigns();
    apiClient.get('/users/me')
      .then(({ data }) => setIsAdmin(data?.user?.role === 'admin'))
      .catch(() => setIsAdmin(false));
  }, []);

  const fetchCampaigns = async () => {
    try {
      const { data } = await apiClient.get('/stats/campaigns/user');
      const collection = data.campaigns || [];
      setCampaigns(collection);
      if (!selectedCampaign && collection.length) {
        setSelectedCampaign(collection[0].id);
      }
    } catch (error) {
      console.error('Не удалось загрузить кампании:', error);
      setStatus('Ошибка загрузки кампаний');
    }
  };

const fetchTargets = async (campaignId) => {
  if (!campaignId) {
    setTargets([]);
    return;
  }
  setTargetsLoading(true);
  try {
    const { data } = await apiClient.get(`/bidder/targets/${campaignId}`);
    setTargets(data.targets || []);
  } catch (error) {
    console.error('Не удалось загрузить цели (keywords/articles):', error);
  } finally {
    setTargetsLoading(false);
  }
};

useEffect(() => {
  if (selectedCampaign) {
    fetchTargets(selectedCampaign);
  }
}, [selectedCampaign]);

  const runAll = async () => {
    setLoading(true);
    setStatus('Запуск правил для всех кампаний...');
    try {
      const { data } = await apiClient.post('/bidder/run');
      setLogs((prev) => [
        { id: Date.now(), text: `Запущено правил: ${data.processed}` },
        ...prev,
      ]);
      setStatus('Правила выполнены');
    } catch (error) {
      console.error('Ошибка запуска bidder:', error);
      setStatus('Не удалось запустить правила');
    } finally {
      setLoading(false);
    }
  };

  const runSingle = async () => {
    if (!selectedCampaign) {
      setStatus('Выберите кампанию');
      return;
    }
    setLoading(true);
    setStatus('Применяем правила к кампании...');
    try {
      const { data } = await apiClient.post(`/bidder/run/${selectedCampaign}`);
      if (data.result) {
        setLogs((prev) => [
          {
            id: Date.now(),
            text: `Кампания ${data.campaignId} обновлена (${data.result.adjustments?.length || 0} ключей)`,
          },
          ...prev,
        ]);
      }
      setStatus('Правила применены');
    } catch (error) {
      console.error('Ошибка запуска bidder:', error);
      setStatus('Не удалось применить правила');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="max-w-4xl mx-auto p-6 space-y-6">
      <h2 className="text-2xl font-bold">Автоматический биддер</h2>
      <div className="grid md:grid-cols-2 gap-4">
        <div className="space-y-2">
          <label className="text-sm font-semibold">Кампания:</label>
          <select
            className="w-full border rounded px-3 py-2"
            value={selectedCampaign}
            onChange={(e) => setSelectedCampaign(e.target.value)}
          >
            <option value="">-- Выберите кампанию --</option>
            {campaigns.map((campaign) => (
              <option key={campaign.id} value={campaign.id}>
                {campaign.name || `Кампания ${campaign.advert_id}`} (#{campaign.advert_id})
              </option>
            ))}
          </select>
        </div>
        <div className="space-y-2">
          <label className="text-sm font-semibold">Действия:</label>
          <div className="flex gap-2">
            <button
              onClick={runSingle}
              disabled={loading}
              className="flex-1 bg-[#059669] text-white py-2 rounded hover:bg-[#047857]"
            >
              Применить к кампании
            </button>
            {isAdmin && (
              <button
                onClick={runAll}
                disabled={loading}
                className="flex-1 bg-[#2563eb] text-white py-2 rounded hover:bg-[#1d4ed8]"
              >
                Применить ко всем
              </button>
            )}
          </div>
        </div>
      </div>

      {status && <p className="text-sm text-gray-600">{status}</p>}

      <div className="bg-white border rounded shadow-sm p-4 space-y-3">
        <div className="flex justify-between items-center">
          <h3 className="text-lg font-semibold">Цели биддера (ключевые слова / артикулам)</h3>
          {targetsLoading && <span className="text-xs text-gray-500">обновляется…</span>}
        </div>
        {targetsLoading ? (
          <div className="text-sm text-gray-500">Загружаем список целей...</div>
        ) : targets.length === 0 ? (
          <div className="text-sm text-gray-500">Нет данных по текущей кампании</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm text-left">
              <thead className="text-xs uppercase text-gray-500 border-b">
                <tr>
                  <th className="py-2">Тип</th>
                  <th className="py-2">Ключевое слово / Артикул</th>
                  <th className="py-2">Текущая ставка</th>
                  <th className="py-2">CPO</th>
                  <th className="py-2">ДРР</th>
                </tr>
              </thead>
              <tbody>
                {targets.slice(0, 10).map((target) => (
                  <tr key={`${target.id}-${target.keyword || target.nm_id}`} className="border-b">
                    <td className="py-2 text-xs text-gray-600">{formatTargetTypeLabel(target.target_type)}</td>
                    <td className="py-2">
                      {target.keyword || (target.nm_id ? `арт. ${target.nm_id}` : '—')}<br />
                      <span className="text-xs text-gray-400">кампания № {target.campaign_id}</span>
                    </td>
                    <td className="py-2">
                      {target.current_bid ? `${target.current_bid.toFixed(2)} ₽` : '—'}
                    </td>
                    <td className="py-2">
                      {target.cpo !== null ? `${target.cpo.toFixed(2)} ₽` : '—'}
                    </td>
                    <td className="py-2">
                      {target.drr !== null ? `${target.drr.toFixed(2)}` : '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {logs.length > 0 && (
        <div className="bg-white border rounded shadow-sm p-4">
          <h3 className="text-lg font-semibold mb-2">Логи</h3>
          <ul className="space-y-1 text-sm">
            {logs.map((entry) => (
              <li key={entry.id} className="text-gray-700">
                • {entry.text}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}
